import {
  STATS_REPORT_TYPE_MERGED,
  getDefaultStatsReportType,
  isAnalysisOnlyAnalystUser,
  isMultiOrgStatsAdmin,
  isNonInputAnalystUser,
  isStatsReportTypeAllowed,
  isSystemStatsAdmin,
  resolveStatsReportTypeForRequest,
} from './statistics-report-type'

/** basicOrganization su ChartRequest / GET crash/all (1 Police, 2 Hospital, 3 Insurance). */
export const STATS_BASIC_ORGANIZATION = Object.freeze({
  POLICE: 1,
  HOSPITAL: 2,
  INSURANCE: 3,
})

const STATS_BASIC_ORGANIZATION_IDS = [
  STATS_BASIC_ORGANIZATION.POLICE,
  STATS_BASIC_ORGANIZATION.HOSPITAL,
  STATS_BASIC_ORGANIZATION.INSURANCE,
]

/** Valore select crash list: dataset merged (GET /crash/all?merged=1). */
export const CRASH_LIST_DATASET_FILTER_MERGED = 'merged'

/** Crash list pending tab con "All categories" (GET /crash/all?merged=0). */
export const CRASH_LIST_DATASET_FILTER_UNMERGED = 'unmerged'

/** Conteggi di entrambe le tab in una sola query (GET /crash/count?merged=both). */
export const CRASH_LIST_DATASET_FILTER_BOTH = 'both'

export const CRASH_LIST_VIEW = Object.freeze({
  PENDING: 'pending',
  VALIDATED: 'validated',
  SOURCE: 'source',
})

/** Merge-source rows merged into a validated master (GET /crash/all?merged=2). */
export const CRASH_LIST_DATASET_FILTER_SOURCE = '2'

/**
 * Analyst (ministero o org Analyst): Crash List solo su dataset merged nazionale.
 * @param {object} [user]
 * @param {Array<object>} [organizations]
 */
export function shouldLockCrashListDatasetToMerged(user, organizations = []) {
  return isAnalysisOnlyAnalystUser(user, organizations)
}

/**
 * Filtro dataset iniziale per Crash List.
 * @param {object} [user]
 */
export function getDefaultCrashListDatasetFilter(_user, _organizations = []) {
  return ''
}

/**
 * Opzioni UI categoria dati (basicOrganization). Passare label già tradotte.
 * @param {{ allCategories: string, police: string, hospital: string, insurance: string, merged?: string }} labels
 * @param {{ includeMerged?: boolean }} [options]
 */
export function getStatsBasicOrganizationOptions(labels, options = {}) {
  const items = [
    { id: '', label: labels.allCategories },
  ]
  if (options.includeMerged && labels.merged) {
    items.push({ id: CRASH_LIST_DATASET_FILTER_MERGED, label: labels.merged })
  }
  items.push(
    { id: STATS_BASIC_ORGANIZATION.POLICE, label: labels.police },
    { id: STATS_BASIC_ORGANIZATION.HOSPITAL, label: labels.hospital },
    { id: STATS_BASIC_ORGANIZATION.INSURANCE, label: labels.insurance },
  )
  return items
}

/**
 * @param {object} [org]
 * @returns {number | null}
 */
function resolveOrganizationBasicId(org) {
  const basic = Number(org?.basic_id ?? org?.basic ?? org?.basicOrganization)
  return STATS_BASIC_ORGANIZATION_IDS.includes(basic) ? basic : null
}

/**
 * Categorie basicOrganization (1 Police, 2 Hospital, 3 Insurance) visibili all'utente.
 * Allineato a scope crash list / Report Type (`getAccessibleStatsReportTypes`).
 * @param {object} [user]
 * @param {Array<object>} [organizations]
 * @returns {number[]}
 */
export function getAccessibleBasicOrganizationIds(user, organizations = []) {
  if (!user) return [...STATS_BASIC_ORGANIZATION_IDS]

  if (isSystemStatsAdmin(user) || isNonInputAnalystUser(user)) {
    return [...STATS_BASIC_ORGANIZATION_IDS]
  }

  const orgIdsToResolve = new Set()
  const userOrgId = Number(user.organization)
  if (Number.isFinite(userOrgId) && userOrgId > 0) {
    orgIdsToResolve.add(userOrgId)
  }

  const userType = Number(user.type)
  if (isMultiOrgStatsAdmin(user) || userType === 2) {
    const managedIds = Array.isArray(user.organization_ids)
      ? user.organization_ids.map(Number).filter((id) => id > 0)
      : []
    managedIds.forEach((id) => orgIdsToResolve.add(id))
  }

  const allOrgs = organizations.filter((o) => Number(o.id ?? o.value) !== -1)
  const basicIds = [
    ...new Set(
      allOrgs
        .filter((o) => orgIdsToResolve.has(Number(o.id ?? o.value)))
        .map(resolveOrganizationBasicId)
        .filter((id) => id != null),
    ),
  ]

  if (basicIds.length > 0) return basicIds

  const userBasic = Number(user.basic_organization)
  if (STATS_BASIC_ORGANIZATION_IDS.includes(userBasic)) {
    return [userBasic]
  }

  return []
}

/**
 * Filtro "Merged" nella crash list: analyst / admin con scope aggregato.
 * @param {object} [user]
 */
export function canUseMergedDatasetFilter(user) {
  if (!user) return false
  if (isSystemStatsAdmin(user) || isNonInputAnalystUser(user) || isMultiOrgStatsAdmin(user)) {
    return true
  }
  // Organization admin (type 2, org operativa)
  if (Number(user.type) === 2 && !isNonInputAnalystUser(user)) {
    return true
  }
  return false
}

/**
 * Opzioni categoria dati filtrate per utente (crash list, dashboard merged, custom chart).
 * @param {{ allCategories: string, police: string, hospital: string, insurance: string, merged?: string }} labels
 * @param {{ user?: object, organizations?: Array<object>, includeMerged?: boolean }} [options]
 */
export function getAccessibleStatsBasicOrganizationOptions(labels, options = {}) {
  const { user, organizations = [], includeMerged = false } = options

  if (isNonInputAnalystUser(user)) {
    if (!includeMerged || !labels.merged) return []
    return [{ id: CRASH_LIST_DATASET_FILTER_MERGED, label: labels.merged }]
  }

  const allowedBasicIds = getAccessibleBasicOrganizationIds(user, organizations)
  const showMerged = includeMerged && canUseMergedDatasetFilter(user)
  const allOptions = getStatsBasicOrganizationOptions(labels, { includeMerged: showMerged })

  const canSeeAllCategories =
    isSystemStatsAdmin(user) ||
    allowedBasicIds.length > 1

  return allOptions.filter((opt) => {
    if (opt.id === '') {
      return canSeeAllCategories || allowedBasicIds.length > 0
    }
    if (opt.id === CRASH_LIST_DATASET_FILTER_MERGED) {
      return showMerged
    }
    return allowedBasicIds.includes(Number(opt.id))
  })
}

/**
 * @param {number | string | null | undefined} value
 * @returns {number | null}
 */
export function parseStatsBasicOrganization(value) {
  if (value === '' || value == null) return null
  const n = Number(value)
  if (!Number.isFinite(n) || n <= 0) return null
  return n
}

/**
 * basicOrganization è valido solo con reportType Merged (0) o legacy ministero (backend).
 * @param {number} reportType
 */
export function canUseBasicOrganizationWithReportType(reportType) {
  return Number(reportType) <= STATS_REPORT_TYPE_MERGED
}

/**
 * Parametri top-level merged / basicOrganization (parità GET /crash/all).
 * @param {{ reportType: number, basicOrganization?: number | string | null }} params
 * @returns {{ merged?: number, basicOrganization?: number }}
 */
export function buildChartRequestDatasetExtras({ reportType, basicOrganization }) {
  const rt = Number(reportType)
  /** @type {{ merged?: number, basicOrganization?: number }} */
  const extras = {}

  if (rt === STATS_REPORT_TYPE_MERGED) {
    extras.merged = 1
  }

  const basic = parseStatsBasicOrganization(basicOrganization)
  if (basic != null && canUseBasicOrganizationWithReportType(rt)) {
    extras.basicOrganization = basic
  }

  return extras
}

/**
 * Query string params per GET /crash/all a partire dal valore della tendina categoria:
 * filtro dataset → `merged`, categoria org → `basicOrganization=1|2|3`.
 * @param {{ datasetFilter?: string | number | null, user?: object }} params
 * @returns {Record<string, string | number>}
 */
export function buildCrashListDatasetQueryParams({ datasetFilter, user }) {
  /** @type {Record<string, string | number>} */
  const params = {}

  if (shouldLockCrashListDatasetToMerged(user)) {
    params.merged = 1
    return params
  }

  const filter = datasetFilter ?? ''

  if (filter === CRASH_LIST_DATASET_FILTER_MERGED) {
    params.merged = 1
    return params
  }

  if (filter === CRASH_LIST_DATASET_FILTER_UNMERGED) {
    params.merged = 0
    return params
  }

  if (filter === CRASH_LIST_DATASET_FILTER_SOURCE) {
    params.merged = 2
    return params
  }

  if (filter === CRASH_LIST_DATASET_FILTER_BOTH) {
    params.merged = CRASH_LIST_DATASET_FILTER_BOTH
    return params
  }

  const basic = parseStatsBasicOrganization(filter)
  if (basic != null) params.basicOrganization = basic

  return params
}

/**
 * Le due tab leggono lo stesso dataset validato: l'analista vincolato al dato nazionale
 * e il filtro "Merged" non hanno incidenti in attesa da mostrare.
 * @param {{ datasetFilter?: string | number | null, lockDatasetToMerged?: boolean }} params
 */
export function crashListTabsShareValidatedDataset({
  datasetFilter,
  lockDatasetToMerged = false,
}) {
  return lockDatasetToMerged || datasetFilter === CRASH_LIST_DATASET_FILTER_MERGED
}

/**
 * Query string params GET /crash/all per la tab Pending / Validated, categoria inclusa.
 * La tab validati corrisponde al dataset "Merged": incidenti nazionali consolidati,
 * eventualmente ristretti alla categoria selezionata.
 * @param {{ view: string, datasetFilter?: string | number | null, lockDatasetToMerged?: boolean, user?: object }} params
 * @returns {Record<string, string | number>}
 */
export function buildCrashListViewQueryParams({
  view,
  datasetFilter,
  lockDatasetToMerged = false,
  user,
}) {
  const isLocked = lockDatasetToMerged || shouldLockCrashListDatasetToMerged(user)
  if (crashListTabsShareValidatedDataset({ datasetFilter, lockDatasetToMerged: isLocked })) {
    return buildCrashListDatasetQueryParams({ datasetFilter: CRASH_LIST_DATASET_FILTER_MERGED, user })
  }

  let datasetFilterForView = CRASH_LIST_DATASET_FILTER_UNMERGED
  if (view === CRASH_LIST_VIEW.VALIDATED) {
    datasetFilterForView = CRASH_LIST_DATASET_FILTER_MERGED
  } else if (view === CRASH_LIST_VIEW.SOURCE) {
    datasetFilterForView = CRASH_LIST_DATASET_FILTER_SOURCE
  }

  const params = buildCrashListDatasetQueryParams({
    datasetFilter: datasetFilterForView,
    user,
  })
  const basic = parseStatsBasicOrganization(datasetFilter)
  if (basic != null) params.basicOrganization = basic

  return params
}

/**
 * Tendina categoria dati: utile solo se l'utente vede più categorie basic (Police/Hospital/Insurance).
 * @param {object} [user]
 * @param {Array<object>} [organizations]
 */
export function shouldShowCrashListCategoryDropdown(user, organizations = []) {
  if (shouldLockCrashListDatasetToMerged(user, organizations)) return false
  if (isSystemStatsAdmin(user)) return true
  return getAccessibleBasicOrganizationIds(user, organizations).length > 1
}

/**
 * Query string params GET /crash/count per i contatori di entrambe le tab.
 * @param {{ datasetFilter?: string | number | null, lockDatasetToMerged?: boolean, user?: object }} params
 * @returns {Record<string, string | number>}
 */
export function buildCrashListTabCountsQueryParams({
  datasetFilter,
  lockDatasetToMerged = false,
  user,
}) {
  const isLocked = lockDatasetToMerged || shouldLockCrashListDatasetToMerged(user)
  if (crashListTabsShareValidatedDataset({ datasetFilter, lockDatasetToMerged: isLocked })) {
    return buildCrashListDatasetQueryParams({ datasetFilter: CRASH_LIST_DATASET_FILTER_MERGED, user })
  }

  const params = buildCrashListDatasetQueryParams({
    datasetFilter: CRASH_LIST_DATASET_FILTER_BOTH,
    user,
  })
  const basic = parseStatsBasicOrganization(datasetFilter)
  if (basic != null) params.basicOrganization = basic

  return params
}

/**
 * Arricchisce payload POST /Stats con reportType risolto e merged/basicOrganization.
 * @param {object} payload
 * @param {object} context
 * @param {object} [context.user]
 * @param {Array<object>} [context.organizations]
 * @param {number} [context.reportType]
 * @param {number | string | null} [context.basicOrganization]
 */
export function enrichStatsRequestPayload(
  payload,
  { user, organizations = [], reportType, basicOrganization },
) {
  const resolvedReportType = resolveStatsReportTypeForRequest(
    user,
    reportType ?? payload?.chartField?.reportType,
    organizations,
  )

  if (
    !isStatsReportTypeAllowed(resolvedReportType, user, organizations) &&
    user
  ) {
    const fallback = getDefaultStatsReportType(user)
    return enrichStatsRequestPayload(payload, {
      user,
      organizations,
      reportType: fallback,
      basicOrganization: canUseBasicOrganizationWithReportType(fallback)
        ? basicOrganization
        : null,
    })
  }

  const chartField = payload?.chartField
    ? { ...payload.chartField, reportType: resolvedReportType }
    : { reportType: resolvedReportType }

  const datasetExtras = buildChartRequestDatasetExtras({
    reportType: resolvedReportType,
    basicOrganization: canUseBasicOrganizationWithReportType(resolvedReportType)
      ? basicOrganization
      : null,
  })

  return {
    ...payload,
    ...datasetExtras,
    chartField,
  }
}
